/**
 * ImageThumbnailGrid Component
 * Displays a grid of image thumbnails with lightbox functionality
 */

import { useState, useCallback } from 'react';
import { Dialog, DialogContent } from './dialog';
import { Button } from './button';
import { ChevronLeft, ChevronRight, X, Download, Trash2, Maximize2 } from 'lucide-react';

interface ImageItem {
  id: string;
  url: string;
  originalFilename?: string;
  uploadedAt?: number;
}

interface ImageThumbnailGridProps {
  images: ImageItem[];
  onDelete?: (id: string) => void;
  showDeleteButton?: boolean;
  columns?: 2 | 3 | 4;
  emptyMessage?: string;
}

export function ImageThumbnailGrid({
  images,
  onDelete,
  showDeleteButton = false,
  columns = 3,
  emptyMessage = 'No hay imagenes',
}: ImageThumbnailGridProps) {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const selectedImage = selectedIndex !== null ? images[selectedIndex] : null;

  const handlePrev = useCallback(() => {
    if (selectedIndex === null) return;
    setSelectedIndex(selectedIndex > 0 ? selectedIndex - 1 : images.length - 1);
  }, [selectedIndex, images.length]);

  const handleNext = useCallback(() => {
    if (selectedIndex === null) return;
    setSelectedIndex(selectedIndex < images.length - 1 ? selectedIndex + 1 : 0);
  }, [selectedIndex, images.length]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'Escape') setSelectedIndex(null);
    },
    [handlePrev, handleNext]
  );

  const handleDownload = useCallback(() => {
    if (!selectedImage) return;
    const link = document.createElement('a');
    link.href = selectedImage.url;
    link.download = selectedImage.originalFilename || `imagen-${selectedImage.id}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [selectedImage]);

  const handleDelete = useCallback(
    (id: string, e?: React.MouseEvent) => {
      e?.stopPropagation();
      if (onDelete) {
        onDelete(id);
        // If we're in the lightbox and deleted the current image, close it
        if (selectedIndex !== null && images[selectedIndex]?.id === id) {
          if (images.length === 1) {
            setSelectedIndex(null);
          } else if (selectedIndex === images.length - 1) {
            setSelectedIndex(selectedIndex - 1);
          }
        }
      }
    },
    [onDelete, selectedIndex, images]
  );

  const columnClass = {
    2: 'grid-cols-2',
    3: 'grid-cols-2 sm:grid-cols-3',
    4: 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4',
  }[columns];

  if (images.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>{emptyMessage}</p>
      </div>
    );
  }

  return (
    <>
      <div className={`grid ${columnClass} gap-3`}>
        {images.map((image, index) => (
          <div
            key={image.id}
            className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer group"
            onClick={() => setSelectedIndex(index)}
          >
            <img
              src={image.url}
              alt={image.originalFilename || `Imagen ${index + 1}`}
              className="w-full h-full object-cover transition-transform group-hover:scale-105"
              loading="lazy"
            />

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
              <Maximize2 className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>

            {/* Delete button */}
            {showDeleteButton && onDelete && (
              <button
                type="button"
                onClick={(e) => handleDelete(image.id, e)}
                className="absolute top-2 right-2 p-1.5 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 z-10"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Lightbox */}
      <Dialog open={selectedIndex !== null} onOpenChange={() => setSelectedIndex(null)}>
        <DialogContent
          className="max-w-4xl w-full h-[90vh] p-0 bg-black/95"
          onKeyDown={handleKeyDown}
        >
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close button */}
            <button
              type="button"
              onClick={() => setSelectedIndex(null)}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Navigation */}
            {images.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrev}
                  className="absolute left-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
                <button
                  type="button"
                  onClick={handleNext}
                  className="absolute right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors z-10"
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
              </>
            )}

            {/* Image */}
            {selectedImage && (
              <img
                src={selectedImage.url}
                alt={selectedImage.originalFilename || 'Imagen'}
                className="max-w-full max-h-[80vh] object-contain"
              />
            )}

            {/* Bottom toolbar */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg p-2">
              <span className="text-white text-sm px-2">
                {selectedIndex !== null ? selectedIndex + 1 : 0} / {images.length}
              </span>
              <div className="w-px h-6 bg-white/20" />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleDownload}
                className="text-white hover:bg-white/20"
              >
                <Download className="h-4 w-4 mr-1" />
                Descargar
              </Button>
              {showDeleteButton && onDelete && selectedImage && (
                <>
                  <div className="w-px h-6 bg-white/20" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(selectedImage.id)}
                    className="text-red-400 hover:bg-white/20 hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Eliminar
                  </Button>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
